import React, { FC, useEffect } from 'react';
import { Form, Input, Drawer, Button, Space, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { editFieldDataFormEnum } from '../../slice/types';
import { selectEditFormState, selectFieldNotes } from '../../slice/selectors';
import { useFieldSlice } from '../../slice';
import { selectCompanies } from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';

const EDIT_FIELD_FORM_ID = 'editFieldForm';

interface IProps {
  fieldName: string;
  companyId: number;
  fieldId2: number;
}

const EditFieldDataForm: FC<IProps> = ({ fieldId2, companyId, fieldName }) => {
  const { t } = useTranslation();

  const { isOpen, loading } = useSelector(selectEditFormState);
  const companies = useSelector(selectCompanies);
  const envId = useSelector(selectSelectedEnvId);
  const notes = useSelector(selectFieldNotes);

  const { actions } = useFieldSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      [editFieldDataFormEnum.fieldName]: fieldName,
      [editFieldDataFormEnum.companyId]: companyId,
      [editFieldDataFormEnum.notes]: notes,
    });
  }, [form, companyId, fieldName, notes]);

  const onFinish = (values: {
    fieldName: string;
    companyId: number;
    notes?: string;
  }) => {
    if (envId) {
      dispatch(
        actions.submitEditFieldData({
          envId,
          ...values,
          fieldId2,
          notes: values.notes || '',
        }),
      );
    }
  };

  const onCloseForm = () => {
    dispatch(actions.toggleEditFieldDataForm(false));
  };

  return (
    <Drawer
      title={t('Field.EditForm.EditFieldData', 'Edit field data')}
      visible={isOpen}
      forceRender={true}
      onClose={onCloseForm}
      destroyOnClose
      placement="right"
      width={400}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onCloseForm}>
              {t('Field.EditForm.Cancel', 'Cancel')}
            </Button>

            <Button
              loading={loading}
              form={EDIT_FIELD_FORM_ID}
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {t('Field.EditForm.Save', 'Save')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark={false}
        id={EDIT_FIELD_FORM_ID}
        onFinish={onFinish}
      >
        <Form.Item
          label={t('Field.EditForm.FieldName', 'Field name')}
          name={editFieldDataFormEnum.fieldName}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Field.EditForm.FieldName', 'Field name')} />
        </Form.Item>

        <Form.Item
          label={t('Field.EditForm.Company', 'Company')}
          name={editFieldDataFormEnum.companyId}
          rules={[{ required: true }]}
        >
          <Select
            placeholder={t('Field.EditForm.Company', 'Company')}
            getPopupContainer={trigger => trigger.parentNode}
          >
            {companies.map(company => (
              <Select.Option key={company.id} value={company.id}>
                {company.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t('Field.EditForm.Notes', 'Notes')}
          name={editFieldDataFormEnum.notes}
        >
          <Input.TextArea
            rows={4}
            placeholder={t('Field.EditForm.Notes', 'Notes')}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditFieldDataForm;
